import { useMemo } from "react";
import { Tooltip } from "react-tooltip";

import { QuestionMarkCircleIcon } from "@heroicons/react/24/solid";
import {
    ChevronDownIcon,
    ChevronUpIcon,
    ListBulletIcon,
    PlusIcon,
    TrashIcon,
    XMarkIcon
} from "@heroicons/react/24/outline";

import {
    IContextBase,
    IContextField
} from "../lib/types";
import {
    classNames,
    isFlatField
} from "../lib/utils";
import { CONTEXT_TYPES } from "../lib/consts";
import {
    IEndpointOutputNameElement,
    IEndpointSplitByField,
    IScrapesRenderColumnConfig,
    IScrapesRenderConfig
} from "../lib/backend/extractions.types.generated";

import { Dropdown } from "./Dropdown";
import { IEndpointSplitByFieldEx } from "../screens/NewEndpoint";
import { Checkbox } from "./Checkbox";
import {
    Button,
    ButtonGroup,
    IconButton
} from "./Button";
import { Textbox } from "./Textbox";
import { EndpointOutputColumns } from "./EndpointOutputColumns";
import { FullScreenDialog } from "./FullScreen";
import { EndpointOutputName } from "./EndpointOutputName";
import { TbTable } from "react-icons/tb";

type IEndpointCustomFileNameProps = {
    contexts: IContextBase[]
    custom_file_name: IEndpointOutputNameElement[];
    split_by_field?: IEndpointSplitByField[];
    updateCustomFileName: (custom_file_name: IEndpointOutputNameElement[]) => void;
}

export function EndpointCustomFileName({
    contexts,
    custom_file_name,
    split_by_field,
    updateCustomFileName
}: IEndpointCustomFileNameProps) {
    const split_by_field_data: IEndpointSplitByFieldEx[] = useMemo(() => {
        const new_split_by_field_data: IEndpointSplitByFieldEx[] = [];
        for (const context of contexts || []) {
            const split = split_by_field?.find((split) => split.context_uuid === context.uuid);
            if (split === undefined) { continue; }
            const field = context.fields.find((field) => field.uuid === split.field_uuid);
            if (field === undefined) { continue; }
            new_split_by_field_data.push({
                context_uuid: context.uuid,
                context,
                field_uuid: field.uuid,
                field_name: field.name,
                required: split.required ?? false,
                context_type: split.context_type
            });
        }
        return new_split_by_field_data;
    }, [contexts, split_by_field]);

    const addOutputNameElement = (element: IEndpointOutputNameElement) => {
        const new_element = { ...element };
        updateCustomFileName([...custom_file_name, new_element]);
    }

    const removeOutputNameElement = (element_idx: number) => {
        const new_elements = [...(custom_file_name || [])];
        new_elements.splice(element_idx, 1);
        updateCustomFileName(new_elements);
    }

    return <div className="p-4 rounded-lg border border-gray-200 bg-gray-50 max-w-4xl">
        <div className="text-sm leading-6 font-normal">
            <EndpointOutputName
                title={"Filename"}
                contexts={contexts}
                split_fields_data={split_by_field_data}
                elements={custom_file_name}
                placeholder={`Use default "extractions"`}
                addElement={addOutputNameElement}
                removeElement={removeOutputNameElement} />
        </div>
    </div>;
}


type IEndpointCustomOutputSheetsProps = {
    prefix: string;
    in_a_box: boolean;
    contexts: IContextBase[]
    custom_output_sheets: IScrapesRenderConfig[];
    split_by_field?: IEndpointSplitByField[];
    is_committing: boolean;
    tooltip?: string;
    updateOutputSheets: (custom_output_sheets: IScrapesRenderConfig[]) => void;
}

export function EndpointCustomOutputSheets({
    prefix,
    in_a_box,
    contexts,
    custom_output_sheets,
    split_by_field,
    is_committing,
    tooltip,
    updateOutputSheets
}: IEndpointCustomOutputSheetsProps) {
    const split_by_field_data: IEndpointSplitByFieldEx[] = useMemo(() => {
        const new_split_by_field_data: IEndpointSplitByFieldEx[] = [];
        for (const context of contexts || []) {
            const split = split_by_field?.find((split) => split.context_uuid === context.uuid);
            if (split === undefined) { continue; }
            const field = context.fields.find((field) => field.uuid === split.field_uuid);
            if (field === undefined) { continue; }
            new_split_by_field_data.push({
                context_uuid: context.uuid,
                context,
                field_uuid: field.uuid,
                field_name: field.name,
                required: split.required ?? false,
                context_type: split.context_type
            });
        }
        return new_split_by_field_data;
    }, [contexts, split_by_field]);

    const handleOutputSheetsChange = (checked: boolean) => {
        if (checked) {
            addSheet();
        } else {
            updateOutputSheets([]);
        }
    }

    const getValidContextSheetFields = (context: IContextBase): IContextField[] => {
        return context.fields
            .filter((field) =>
                context.type !== CONTEXT_TYPES.hierarchical ||
                isFlatField(field))
    }

    const getValidContextSheetColumns = (context: IContextBase): IScrapesRenderColumnConfig[] => {
        return getValidContextSheetFields(context).map((field) => ({
            name: field.name,
            type: "field",
            field: { context_uuid: context.uuid, field_uuid: field.uuid }
        }));
    }

    const addSheet = () => {
        // check if we have at least one context
        if (contexts.length === 0) { return; }
        const first_context = contexts[0];
        // add new sheet
        const old_custom_output_sheets = custom_output_sheets;
        updateOutputSheets([...old_custom_output_sheets, {
            name: first_context.name,
            context_uuid: first_context.uuid,
            columns: getValidContextSheetColumns(first_context)
        }]
        );
    };

    const changeSheetContext = (sheet_idx: number, context_uuid: string) => {
        // find context
        const context = contexts?.find((context) => context.uuid === context_uuid);
        if (context === undefined) { return; }
        // get old context and see if old name matches the old context name
        const old_context = contexts?.find((context) => context.uuid === custom_output_sheets[sheet_idx].context_uuid);
        const old_name = custom_output_sheets[sheet_idx]?.name;
        const is_context_name = (old_context?.name === old_name);
        // update sheet
        const new_sheets = [...custom_output_sheets];
        new_sheets[sheet_idx].context_uuid = context_uuid;
        new_sheets[sheet_idx].columns = getValidContextSheetColumns(context);
        // we change name only if it was the same as old context name
        if (is_context_name) { new_sheets[sheet_idx].name = context.name; }
        updateOutputSheets(new_sheets);
    };

    const changeSheetName = (sheet_idx: number, name: string) => {
        const new_sheets = [...custom_output_sheets];
        new_sheets[sheet_idx].name = name;
        updateOutputSheets(new_sheets);
    }

    const deleteSheet = (sheet_idx: number) => {
        const new_sheets = [...custom_output_sheets];
        new_sheets.splice(sheet_idx, 1);
        updateOutputSheets(new_sheets);
    }

    const moveSheet = (curr_idx: number, direction: "up" | "down") => {
        const new_sheets = [...custom_output_sheets];
        const new_idx = direction === "up" ? curr_idx - 1 : curr_idx + 1;
        if (new_idx >= 0 && new_idx < new_sheets.length) {
            const temp = new_sheets[curr_idx];
            new_sheets[curr_idx] = new_sheets[new_idx];
            new_sheets[new_idx] = temp;
            updateOutputSheets(new_sheets);
        }
    };

    const addColumn = (sheet_idx: number) => {
        // find sheet context
        if (contexts.length === 0) { return; }
        const new_sheets = [...custom_output_sheets];
        const sheet_context = contexts.find((context) => context.uuid === new_sheets[sheet_idx].context_uuid);
        if (sheet_context === undefined) { return; }
        // add new column and use first field as default
        const sheet_fields = getValidContextSheetFields(sheet_context);
        new_sheets[sheet_idx].columns.push({
            name: sheet_fields[0].name,
            type: "field",
            field: {
                context_uuid: sheet_context.uuid,
                field_uuid: sheet_fields[0].uuid
            }
        });
        updateOutputSheets(new_sheets);
    };

    const setColumn = (sheet_idx: number, column_idx: number, column: IScrapesRenderColumnConfig) => {
        const new_sheets = [...custom_output_sheets];
        new_sheets[sheet_idx].columns[column_idx] = column;
        updateOutputSheets(new_sheets);
    };

    const moveColumn = (sheet_idx: number, curr_idx: number, direction: "up" | "down") => {
        const new_sheets = [...custom_output_sheets];
        const new_idx = direction === "up" ? curr_idx - 1 : curr_idx + 1;
        if (new_idx >= 0 && new_idx < new_sheets[sheet_idx].columns.length) {
            const temp = new_sheets[sheet_idx].columns[curr_idx];
            new_sheets[sheet_idx].columns[curr_idx] = new_sheets[sheet_idx].columns[new_idx];
            new_sheets[sheet_idx].columns[new_idx] = temp;
            updateOutputSheets(new_sheets);
        }
    };

    const deleteColumn = (sheet_idx: number, column_idx: number) => {
        const new_sheets = [...custom_output_sheets];
        new_sheets[sheet_idx].columns.splice(column_idx, 1);
        updateOutputSheets(new_sheets);
    };

    const deleteAllButFirstColumn = (sheet_idx: number) => {
        const new_sheets = [...custom_output_sheets];
        new_sheets[sheet_idx].columns = new_sheets[sheet_idx].columns.slice(0, 1);
        updateOutputSheets(new_sheets);
    }

    return <div className={classNames(in_a_box ? "p-4 rounded-lg border border-gray-200 bg-gray-50 max-w-4xl" : "")}>
        <div className="flex flex-row items-start sm:items-start sm:gap-4 pb-6">
            <div className="pt-1 sm:mt-0">
                <Checkbox
                    id={`${prefix}_output_sheets`}
                    aria-describedby={`${prefix}_output_sheets`}
                    checked={custom_output_sheets.length > 0}
                    disabled={contexts.length === 0 || is_committing}
                    setChecked={handleOutputSheetsChange}
                />
            </div>
            <label htmlFor={`${prefix}_output_sheets`} className="flex flex-row items-center text-sm font-medium sm:pt-1.5">
                <p className={classNames(contexts.length > 0 ? "text-gray-900" : "text-gray-400")}>
                    Custom result template
                </p>
                <QuestionMarkCircleIcon
                    className="w-5 h-5 ml-1 text-gray-400"
                    data-tooltip-id="custom-output-sheets-tooltip-id"
                    data-tooltip-html={tooltip ? `<p class="pb-4 max-w-sm">${tooltip}</p>` : `
                        <p class="pb-4 max-w-sm">
                        By default all the fields from the template are included in the attachment.
                        If you want to customize the output, you can do it here. Just list the sheets and columns you
                        want to include in the output, like you would in Excel. Fields with note "multiple values possible",
                        can have multiple values, and only the first value will be used for the output.
                    </p>`}
                />
            </label>
        </div>

        {custom_output_sheets.length > 0 && <div className="">
            <div className="flex flex-row items-center gap-2 px-2">
                <h3 className="text-sm font-semibold leading-6 text-gray-900">Output Sheets</h3>
                <div className="flex-grow" />
                <Button icon={PlusIcon} text="Add Sheet" onClick={addSheet} />
            </div>
            {custom_output_sheets.map((sheet, idx) => (
                <div key={idx} className="py-6">
                    <div className="px-2 pb-4 flex flex-row items-center gap-2">
                        <span className="text-sm font-semibold pr-4">Sheet {idx + 1}</span>
                        <div className="flex-grow" />
                        <div className="flex flex-row">
                            <ButtonGroup
                                buttons={[
                                    { icon: ChevronUpIcon, text: "", disabled: idx === 0, tooltip: "Move up", onClick: () => moveSheet(idx, "up") },
                                    { icon: ChevronDownIcon, text: "", disabled: idx === custom_output_sheets.length - 1, tooltip: "Move down", onClick: () => moveSheet(idx, "down") },
                                    { icon: TrashIcon, text: "", tooltip: "Remove", onClick: () => deleteSheet(idx) },
                                ]}
                            />
                        </div>
                    </div>
                    <div className="px-2 pb-4 grid grid-cols-2 items-center gap-2">
                        <div>
                            <span className="text-sm pr-4">Sheet name:</span>
                            <Textbox value={sheet.name} onChange={(name) => changeSheetName(idx, name)} />
                        </div>
                        <div>
                            <span className="text-sm pr-4">Base step:</span>
                            <Dropdown
                                values={contexts.map((context, idx) => context.name.length > 0 ? context.name : `Step ${idx + 1}`)}
                                ids={contexts.map((context) => context.uuid)}
                                selected={sheet.context_uuid}
                                onChange={(context_uuid) => changeSheetContext(idx, context_uuid)} />
                        </div>
                    </div>
                    <EndpointOutputColumns
                        context={contexts.find((context) => context.uuid === sheet.context_uuid)}
                        other_contexts={contexts
                            .map((context, idx) => ({ ...context, step_idx: idx }))
                            .filter((context) => sheet.context_uuid !== context.uuid)}
                        endpoint_split_by_field={split_by_field_data}
                        columns={sheet.columns}
                        show_hash_key={false}
                        allow_zero_hash_keys={true}
                        addColumn={() => addColumn(idx)}
                        setColumn={(column_idx, column) => setColumn(idx, column_idx, column)}
                        moveColumn={(curr_idx, direction) => moveColumn(idx, curr_idx, direction)}
                        deleteColumn={(column_idx) => deleteColumn(idx, column_idx)}
                        deleteAllButFirstColumn={() => deleteAllButFirstColumn(idx)}
                    />
                </div>
            ))}
        </div>}
        <Tooltip id="custom-output-sheets-tooltip-id" />
    </div>
}

type IEndpointSplitByFieldProps = {
    prefix: string;
    in_a_box: boolean;
    contexts: IContextBase[]
    split_by_field?: IEndpointSplitByField[];
    is_committing: boolean;
    tooltip?: string;
    updateSplitByField: (split_by_field: IEndpointSplitByField[] | undefined) => void;
}

export function EndpointSplitByField({
    prefix,
    in_a_box,
    contexts,
    split_by_field,
    is_committing,
    tooltip,
    updateSplitByField
}: IEndpointSplitByFieldProps) {
    const split_by_field_data_all: IEndpointSplitByFieldEx[] = useMemo(() => {
        const new_split_by_field_data_all: IEndpointSplitByFieldEx[] = [];
        for (const context of contexts) {
            const split = split_by_field?.find((split) => split.context_uuid === context.uuid) || {
                context_uuid: context.uuid,
                field_uuid: "",
                context_type: context.type === "object" ? "object" : "array"
            };
            const field = context.fields.find((field) => field.uuid === split.field_uuid) || { uuid: "", name: "" };
            new_split_by_field_data_all.push({
                context_uuid: context.uuid,
                context,
                field_uuid: field.uuid,
                field_name: field.name,
                required: split.required ?? false,
                context_type: split.context_type
            });
        }
        return new_split_by_field_data_all;
    }, [contexts, split_by_field]);

    const handleSplitByFieldChange = (value: boolean) => {
        if (value) {
            const split_by_field = contexts
                .filter((context) => context.fields.length > 0)
                .map((context) => ({
                    context_uuid: context.uuid,
                    field_uuid: context.fields[0].uuid,
                    context_type: (context.type === "object" ? "object" : "array") as "object" | "array"
                }));
            updateSplitByField(split_by_field);
        } else {
            updateSplitByField(undefined);
        }
    }

    const handleSplitByFieldDataChange = (context_uuid: string, type: "field_uuid" | "context_type" | "required", value: string | boolean) => {
        if (type === "field_uuid") {
            const field_uuid = value as string;
            if (field_uuid === "") {
                // filter out this context
                updateSplitByField(split_by_field?.filter((split) => split.context_uuid !== context_uuid));
            } else {
                const current_split = split_by_field || [];
                const existing_context_split = current_split.find(split => split.context_uuid === context_uuid);

                if (existing_context_split) {
                    // Update existing split
                    updateSplitByField(current_split.map((split) =>
                        split.context_uuid === context_uuid
                            ? { ...split, field_uuid: field_uuid }
                            : split
                    )
                    );
                } else {
                    // Add new split and sort according to endpoint_contexts order
                    const new_split = [...current_split, {
                        context_uuid,
                        field_uuid,
                        context_type: (contexts.find(c => c.uuid === context_uuid)?.type === "object" ? "object" : "array") as "object" | "array",
                        required: false
                    }];

                    // Sort based on endpoint_contexts order
                    new_split.sort((a, b) => {
                        const a_index = contexts.findIndex(c => c.uuid === a.context_uuid);
                        const b_index = contexts.findIndex(c => c.uuid === b.context_uuid);
                        return a_index - b_index;
                    });

                    updateSplitByField(new_split);
                }
            }
        } else if (type === "context_type") {
            const context_type = value as "array" | "object";
            updateSplitByField(split_by_field?.map((split) => split.context_uuid === context_uuid ? { ...split, context_type } : split));
        } else if (type === "required") {
            const required = value as boolean;
            updateSplitByField(split_by_field?.map((split) => split.context_uuid === context_uuid ? { ...split, required } : split));
        }
    }

    return <div className={classNames(in_a_box ? "p-4 rounded-lg border border-gray-200 bg-gray-50 max-w-4xl" : "")}>
        <div className="flex flex-row items-start sm:items-start sm:gap-4">
            <div className="pt-0.5 sm:mt-0">
                <Checkbox
                    id={`${prefix}_split_by_field`}
                    checked={split_by_field !== undefined}
                    disabled={is_committing}
                    setChecked={(value) => handleSplitByFieldChange(value)} />
            </div>
            <label htmlFor={`${prefix}_split_by_field`} className="flex flex-row items-center text-sm font-medium sm:pt-1.5">
                <span>Split by field</span>
                <QuestionMarkCircleIcon
                    className="w-5 h-5 ml-1 text-gray-400"
                    data-tooltip-id="split-by-field-tooltip-id"
                    data-tooltip-html={tooltip ? `<p class="pb-4 max-w-sm">${tooltip}</p>` : `
                        <p class="pb-4 max-w-sm">
                        You can split the result into multiple individual attachments by selecting one or more split fields.
                        New attachments is created for each unique combination of the split fields. Steps for which
                        no split fields are selected are included in all the results. You can see results
                        by selecting "Results" from the left menu.
                        </p>`}
                />
            </label>
        </div>

        {split_by_field !== undefined && <div className="flex flex-col gap-2 col-span-4 pt-6">
            {split_by_field_data_all.map((split, idx) => <div key={idx} className="grid grid-cols-7 items-center gap-4">
                <div className="col-span-2 text-sm overflow-hidden truncate text-ellipsis" title={split.context.name.length > 0 ? split.context.name : `Step ${idx + 1}`}>
                    {split.context.name.length > 0 ? split.context.name : `Step ${idx + 1}`}
                </div>
                <div className="col-span-5 flex flex-row items-center gap-2">
                    <label htmlFor="field_uuid" className="text-sm">Field</label>
                    <Dropdown
                        values={split.field_uuid === "" ? split.context.fields.map((field) => field.name) : ["[Do not split this step]", ...split.context.fields.map((field) => field.name)]}
                        ids={split.field_uuid === "" ? split.context.fields.map((field) => field.uuid) : ["", ...split.context.fields.map((field) => field.uuid)]}
                        selected={split.field_uuid}
                        placeholder="Do not split this step"
                        disabled={is_committing}
                        onChange={(new_field_uuid: string) => handleSplitByFieldDataChange(split.context.uuid, "field_uuid", new_field_uuid)} />
                    <ButtonGroup
                        buttons={[
                            {
                                icon: TbTable,
                                text: "",
                                tooltip: "Table",
                                selected: split.context_type === "array",
                                onClick: () => handleSplitByFieldDataChange(split.context.uuid, "context_type", "array")
                            },
                            {
                                icon: ListBulletIcon,
                                text: "",
                                tooltip: "List",
                                selected: split.context_type === "object",
                                onClick: () => handleSplitByFieldDataChange(split.context.uuid, "context_type", "object")
                            }
                        ]}
                    />
                    <div className="flex flex-row items-center pl-2">
                        <Checkbox
                            id="required"
                            checked={split.required}
                            disabled={split.field_uuid.length === 0 || is_committing}
                            setChecked={(value) => handleSplitByFieldDataChange(split.context.uuid, "required", value)} />
                        <label htmlFor="required" className={classNames("text-sm pl-2", split.field_uuid.length === 0 ? "text-gray-400" : "text-gray-900")}>
                            Req
                        </label>
                    </div>
                </div>
            </div>)}
        </div>}
        <Tooltip id="split-by-field-tooltip-id" />
    </div>
}

type EndpointCustomOutputProps = {
    title: string;
    contexts: IContextBase[];
    custom_file_name: IEndpointOutputNameElement[];
    custom_output_sheets?: IScrapesRenderConfig[];
    split_by_field?: IEndpointSplitByField[];
    is_committing: boolean;
    split_by_field_tooltip?: string;
    custom_output_sheets_tooltip?: string;
    updateCustomFileName: (custom_file_name: IEndpointOutputNameElement[]) => void;
    updateOutputSheets?: (custom_output_sheets: IScrapesRenderConfig[]) => void;
    updateSplitByField?: (split_by_field?: IEndpointSplitByField[]) => void;
    onClose: () => void;
}

export function EndpointCustomOutput({
    title,
    contexts,
    custom_file_name,
    custom_output_sheets,
    split_by_field,
    is_committing,
    split_by_field_tooltip,
    custom_output_sheets_tooltip,
    updateCustomFileName,
    updateOutputSheets,
    updateSplitByField,
    onClose
}: EndpointCustomOutputProps) {
    return <FullScreenDialog show={true} max_width="4xl" onClose={onClose}>
        <div className="p-4 flex flex-col gap-y-8">
            <div className="flex flex-row items-center gap-x-2">
                <h1 className="text-base font-semibold leading-6 text-gray-900">Custom Template for {title}</h1>
                <div className="flex-grow" />
                <IconButton icon={XMarkIcon} onClick={onClose} size="large" />
            </div>

            <div className="flex flex-col gap-y-4">
                <EndpointCustomFileName
                    contexts={contexts}
                    custom_file_name={custom_file_name}
                    split_by_field={split_by_field}
                    updateCustomFileName={updateCustomFileName}
                />
                {updateSplitByField && <EndpointSplitByField
                    prefix="dialog"
                    in_a_box={true}
                    contexts={contexts}
                    split_by_field={split_by_field}
                    is_committing={is_committing}
                    tooltip={split_by_field_tooltip}
                    updateSplitByField={updateSplitByField}
                />}
                {custom_output_sheets && updateOutputSheets && <EndpointCustomOutputSheets
                    prefix="dialog"
                    in_a_box={true}
                    contexts={contexts}
                    custom_output_sheets={custom_output_sheets}
                    split_by_field={split_by_field}
                    is_committing={is_committing}
                    tooltip={custom_output_sheets_tooltip}
                    updateOutputSheets={updateOutputSheets}
                />}

                <div className="flex flex-row justify-end max-w-4xl">
                    <Button text="Close" highlight={true} onClick={onClose} />
                </div>
            </div>
        </div>
    </FullScreenDialog>
}